import { useMemo, useState } from "react";
import { InputProps } from "./Input";
import Icon from "@/Components/Icon/Icon";
import AiImageBadge from "@/Components/ImageLibrary/AiImageBadge";
import ImageWithFallback from "@/Components/ImageWithFallback";
import UploadImageWithLibraryModal from "@/Components/Modals/UploadImageWithLibraryModal";
import { ImageGuidelines } from "@/Components/Modals/UploadImageWithLibrary/imageGuidelines";
import { isAiImage } from "@/shared/imageLibrary/aiImage";
import { ImageLibraryParams } from "@/shared/imageLibrary/types";
import { useTranslation } from "react-i18next";

import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { reorder } from "@/shared/utils/helpers";
import { MAX_IMAGE_COUNT } from "@/shared/utils/constant";

interface InputImageProps extends InputProps {
  hideErrorMessage?: boolean;
  error?: string;
  label?: string;
  multi?: boolean;
  name: string;
  onchange?: (name: string, images: any[]) => void;
  onDelete?: (name: string, index: number) => void;
  ondragEnd?: (name: string, images: any[]) => void;
  images: any[];
  /** Also lets the user pick from the image library. */
  showLibrary?: boolean;
  libraryParams?: ImageLibraryParams;
  guidelines?: ImageGuidelines;
}

export const StyledInputLabel = "label";
export const StyledInputFile = "input";

const InputImage = ({
  hideErrorMessage = false,
  multi = true,
  error,
  label,
  name,
  onchange,
  onDelete,
  ondragEnd,
  images,
  showLibrary = false,
  libraryParams,
  guidelines,
}: InputImageProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleClickDelete = (indx) => {
    if (onDelete) {
      onDelete(name, indx);
    }
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    if (!destination) {
      return;
    }
    const items = reorder(images[name], source.index, destination.index);
    ondragEnd(name, items);
  };

  const imageUrls = useMemo(() => {
    return images[name]?.map((image) => {
      if (image instanceof File) {
        return {
          url: URL.createObjectURL(image),
          name: image.name,
        };
      } else {
        return {
          url: image,
          name: image,
        };
      }
    });
  }, [images, name]);

  const remaining = multi ? MAX_IMAGE_COUNT - (imageUrls?.length ?? 0) : 1;

  return (
    <div className="mb-[1rem]">
      <button
        type="button"
        disabled={remaining <= 0}
        onClick={() => setIsModalOpen(true)}
        className="inline-block w-full cursor-pointer rounded-[5px] border border-[#ccc] bg-[var(--white)] px-[12px] py-[6px] text-center font-semibold text-[var(--text)] hover:bg-[var(--light)] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {label}
      </button>
      {isModalOpen && (
        <UploadImageWithLibraryModal
          onExit={() => setIsModalOpen(false)}
          onSave={(files) => onchange?.(name, files)}
          title={label}
          showLibrary={showLibrary}
          libraryParams={libraryParams}
          guidelines={guidelines}
          multiple={multi}
          maxFiles={remaining}
        />
      )}
      {!hideErrorMessage && error && (
        <div className="mt-[1rem] text-[1.4rem] text-left text-error">
          {error}
        </div>
      )}

      <div className="bg-[var(--white)]">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable
            droppableId="droppable"
            isDropDisabled={imageUrls.length === 1}
          >
            {(provided, { isDraggingOver }) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{ backgroundColor: isDraggingOver && "lightblue" }}
              >
                {imageUrls.map((item, index) => {
                  return (
                    <Draggable
                      key={item.url}
                      draggableId={item.url}
                      index={index}
                      isDragDisabled={imageUrls.length === 1}
                    >
                      {(provided, { isDragging }) => (
                        <>
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="relative flex h-[100px] w-full items-center justify-between rounded p-[10px]"
                            style={{
                              backgroundColor: isDragging ? "var(--grey)" : "",
                              ...provided.draggableProps.style,
                            }}
                          >
                            <div>
                              <div className="relative inline-block">
                                <ImageWithFallback
                                  style={{
                                    width: "75px",
                                    height: "75px",
                                    objectFit: "cover",
                                  }}
                                  src={item.url}
                                />
                                {isAiImage(item.name) && <AiImageBadge />}
                              </div>
                              <span
                                className="absolute top-[50%] left-[120px]"
                                style={{ transform: "translateY(-50%)" }}
                              >
                                {item?.name?.split("/")?.reverse()?.[0]}
                              </span>
                            </div>
                            <div onClick={() => handleClickDelete(index)}>
                              <Icon
                                className="h-[2.2rem] w-[2.2rem] fill-error"
                                variants="trash"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </Draggable>
                  );
                })}
                {multi &&
                  imageUrls.length > 0 &&
                  imageUrls.length < MAX_IMAGE_COUNT &&
                  !isDraggingOver && (
                    <div className="pb-[10px] px-[10px]">
                      <button
                        type="button"
                        onClick={() => setIsModalOpen(true)}
                        className="inline-block h-[35px] w-[100px] cursor-pointer rounded-[5px] border border-[#ccc] bg-primary px-[12px] py-[6px] text-center font-semibold text-white"
                      >
                        {t(`settings_page.add`)}
                      </button>
                    </div>
                  )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
};

export default InputImage;
